import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import { Input } from '@/components/ui/input';
import { Building, Search, ArrowRight, Loader2 } from 'lucide-react';

export function CompanyProfileNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [companyAccess, setCompanyAccess] = useState<{
    hasAccess: boolean;
    companyId?: string;
    role?: string;
  }>({ hasAccess: false });

  useEffect(() => {
    if (user) {
      checkCompanyAccess();
    }
  }, [user]);

  const checkCompanyAccess = async () => {
    if (!user) return;

    try {
      // Check if user has any active company memberships
      const { data: companyUser, error: accessError } = await supabase
        .from('company_users')
        .select('company_id, role')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (accessError) throw accessError;

      if (companyUser) {
        setCompanyAccess({
          hasAccess: true,
          companyId: companyUser.company_id,
          role: companyUser.role
        });
      } else {
        setCompanyAccess({ hasAccess: false });
      }
    } catch (error) {
      console.error('Error checking company access:', error);
      setCompanyAccess({ hasAccess: false });
    }
  };

  const handleCompanyClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!user) return;

    if (companyAccess.hasAccess && companyAccess.companyId) {
      navigate(`/company/${companyAccess.companyId}`);
    } else {
      setShowJoinDialog(true);
    }
  };

  const handleClaimOrJoinCompany = async (companyId: string) => {
    if (!user) return;
    
    setIsRequesting(true);
    try {
      const { data, error } = await supabase.rpc('claim_or_request_company', {
        p_company_id: companyId,
        p_user_id: user.id
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Request Response',
        description: data.message,
      });
      
      if (!data.join_request_id) {
        setCompanyAccess({ hasAccess: true, companyId });
        navigate(`/company/${companyId}`);
      }
      setShowJoinDialog(false);
    } catch (error: any) {
      console.error('Error in claim or join company:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to process the request',
      });
    } finally {
      setIsRequesting(false);
    }
  };

  const searchCompanies = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      // First get companies that match the search
      const { data, error } = await supabase
        .from('companies')
        .select(`
          id, 
          name, 
          industry, 
          headquarters_location
        `)
        .ilike('name', `%${query}%`)
        .limit(5);

      if (error) throw error;

      // Then for each company, check if it has an admin
      const companiesWithAdminInfo = await Promise.all(
        (data || []).map(async (company) => {
          const { data: adminData } = await supabase
            .from('company_users')
            .select('user_id')
            .eq('company_id', company.id)
            .eq('role', 'admin')
            .eq('is_active', true)
            .maybeSingle();

          return {
            ...company,
            has_admin: !!adminData
          };
        })
      );

      setSearchResults(companiesWithAdminInfo);
    } catch (error) {
      console.error('Error searching companies:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to search companies',
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      <Button 
        variant="ghost" 
        className="text-sm font-medium hover:text-primary transition-colors"
        onClick={handleCompanyClick}
      >
        Company Profile
      </Button>

      {/* Join / Claim Company Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Join or Claim Company</DialogTitle>
            <DialogDescription>
              Search for your company. If it exists, you can request to join it.
              If you create a new company and verify your work email within 5 minutes,
              you will be auto-approved. Otherwise, your request will require manual approval.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search companies..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchCompanies(e.target.value);
                  }}
                  className="pl-10"
                />
              </div>

              {loading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-2">
                  {searchResults.map((company) => (
                    <Card 
                      key={company.id} 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{company.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {company.industry} • {company.headquarters_location}
                            </p>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleClaimOrJoinCompany(company.id)}
                            disabled={isRequesting}
                          >
                            {isRequesting ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Processing...
                              </>
                            ) : (
                              <>
                                {company.has_admin ? 'Request Access' : 'Claim Company'}
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : searchQuery ? (
                <div className="text-center py-4">
                  <Building className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No companies found</p>
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoinDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}